import React, { createContext, useContext, useState } from "react";

const TabsContext = createContext();

/**
 * Tabs: The container for the tabs. It manages the active tab value.
 *
 * Props:
 * - defaultValue: The initially active tab value.
 * - className: Optional additional class names.
 */
export function Tabs({ children, defaultValue, className = "" }) {
  const [activeValue, setActiveValue] = useState(defaultValue);

  return (
    <TabsContext.Provider value={{ activeValue, setActiveValue }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

/**
 * TabsList: A container for the tab triggers.
 *
 * Props:
 * - className: Optional additional class names.
 */
export function TabsList({ children, className = "" }) {
  return <div className={className}>{children}</div>;
}

/**
 * TabsTrigger: The clickable tab element.
 *
 * Props:
 * - value: The unique value for this tab.
 * - className: Optional additional class names.
 * - ...props: Any additional props (like onClick) you may pass.
 *
 * When clicked, it sets the active tab in the context.
 */
export function TabsTrigger({ children, value, className = "", ...props }) {
  const { activeValue, setActiveValue } = useContext(TabsContext);
  const isActive = activeValue === value;

  return (
    <button
      className={`
        ${className}
        px-4 py-2 font-medium focus:outline-none
        ${isActive ? "text-red-600 border-b-2 border-red-600" : "text-gray-600 border-b-2 border-transparent hover:text-red-600"}
      `}
      onClick={(e) => {
        if (props.onClick) props.onClick(e);
        setActiveValue(value);
      }}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * TabsContent: The container for the content of each tab.
 *
 * Props:
 * - value: The unique value corresponding to the tab.
 * - className: Optional additional class names.
 *
 * It only renders its children if its value matches the active tab.
 */
export function TabsContent({ children, value, className = "", ...props }) {
  const { activeValue } = useContext(TabsContext);
  return activeValue === value ? (
    <div className={className} {...props}>
      {children}
    </div>
  ) : null;
}
